/* Vendas de hoje ao vivo (espelha painel ML).
   Total destaque + métricas-chave + tendências hoje vs ontem + top produtos. */
import { useQuery } from '@tanstack/react-query'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { Loader2, Eye, Users, ShoppingCart, TrendingUp, Package, Boxes } from 'lucide-react'
import { api, formatMoney } from '@/lib/api'

type Resp = {
  data: string
  atualizado_em: string
  hoje: {
    vendas_brutas: number
    unidades: number
    qtd_vendas: number
    qtd_canceladas: number
    compradores_unicos: number
    visitas: number
    conversao_pct: number
  }
  ontem: { vendas_brutas: number; qtd_vendas: number; visitas: number }
  tendencia_horas: {
    hoje: { hora: number; vendas: number; qtd: number }[]
    ontem: { hora: number; vendas: number; qtd: number }[]
  }
  top_produtos: {
    produto_id: number
    codigo: string
    descricao: string
    ml_item_id: string | null
    estoque_atual: number
    qtd: number
    receita: number
  }[]
  tem_visitas: boolean
}

function ChavePill({ icone, valor, label, sub }: any) {
  return (
    <div className="flex items-center gap-3 bg-white border border-ink-200 rounded-md p-3">
      <div className="text-ink-400 shrink-0">{icone}</div>
      <div>
        <div className="text-xl font-bold text-ink-900 leading-tight">{valor}</div>
        <div className="text-[11px] text-ink-500">{label}</div>
        {sub && <div className="text-[10px] text-ink-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  )
}

export default function VendasAoVivoMLPage() {
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ['ml-ao-vivo'],
    queryFn: async () => (await api.get('/marketplace/ml-painel/ao-vivo')).data,
    refetchInterval: 60_000, // 1min — fica "ao vivo"
  })

  if (isLoading || !data) {
    return <div className="p-10 text-center text-ink-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Carregando…</div>
  }

  // Merge horas hoje + ontem em pontos por hora 0-23
  const horas = Array.from({ length: 24 }, (_, h) => {
    const ho = data.tendencia_horas.hoje.find(x => x.hora === h)
    const on = data.tendencia_horas.ontem.find(x => x.hora === h)
    return { hora: `${h.toString().padStart(2, '0')}:00`, hoje: ho?.vendas || 0, ontem: on?.vendas || 0 }
  })

  return (
    <div className="p-4 lg:p-6 bg-yellow-50 min-h-screen space-y-4">
      {/* Header com valor destaque */}
      <div className="bg-yellow-300 border-2 border-yellow-400 rounded-lg p-6 text-center">
        <div className="text-sm text-yellow-900 mb-1">Vendas de hoje ao vivo</div>
        <div className="text-4xl font-bold text-ink-900">{formatMoney(data.hoje.vendas_brutas)}</div>
        <div className="text-xs text-ink-700 mt-1">
          {Math.round(data.hoje.unidades)} unidade{data.hoje.unidades !== 1 ? 's' : ''} · atualizado às {data.atualizado_em}
        </div>
      </div>

      {/* Linha 2: Métricas-chave + Gráfico + Produtos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Coluna 1: Métricas-chave */}
        <div className="bg-white border border-ink-200 rounded-md p-4 space-y-2">
          <div className="text-sm font-semibold text-ink-700 mb-2">Métricas-chave</div>
          <ChavePill icone={<Eye size={20} />} valor={data.hoje.visitas.toLocaleString('pt-BR')} label="Visitas únicas" />
          <ChavePill icone={<Users size={20} />} valor={data.hoje.compradores_unicos} label="Total de compradores" />
          <ChavePill icone={<ShoppingCart size={20} />} valor={data.hoje.qtd_vendas} label="Quantidade de vendas" sub={`${Math.round(data.hoje.unidades)} unid · ${formatMoney(data.hoje.vendas_brutas)}`} />
          <ChavePill icone={<TrendingUp size={20} />} valor={`${data.hoje.conversao_pct.toFixed(2)}%`} label="Conversão" />
        </div>

        {/* Coluna 2: Gráfico */}
        <div className="bg-white border border-ink-200 rounded-md p-4 lg:col-span-1">
          <div className="text-sm font-semibold text-ink-700 mb-2">Tendências em vendas brutas</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={horas}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="hora" tick={{ fontSize: 9 }} interval={2} />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => formatMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line type="monotone" dataKey="hoje" stroke="#ec4899" strokeWidth={2} name="Hoje" dot={false} />
              <Line type="monotone" dataKey="ontem" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="3 3" name="Ontem" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Coluna 3: Top produtos */}
        <div className="bg-white border border-ink-200 rounded-md p-4">
          <div className="text-sm font-semibold text-ink-700 mb-2">Produtos mais vendidos</div>
          {data.top_produtos.length === 0 ? (
            <div className="text-xs text-ink-400 italic">Sem vendas hoje ainda</div>
          ) : (
            <div className="space-y-2">
              {data.top_produtos.map((p, i) => (
                <div key={p.produto_id || i} className="flex items-start gap-2 border-b border-ink-100 pb-2 last:border-0 last:pb-0">
                  <div className="text-xs font-bold text-amber-600 w-4 shrink-0">{i + 1}</div>
                  <div className="w-9 h-9 bg-ink-100 rounded shrink-0 flex items-center justify-center">
                    <Package size={16} className="text-ink-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-ink-800 truncate" title={p.descricao}>{p.descricao}</div>
                    <div className="text-[10px] text-ink-500">
                      Vendas no dia {formatMoney(p.receita)} · {Math.round(p.qtd)} unid
                    </div>
                    <div className="text-[10px] text-ink-400 inline-flex items-center gap-1">
                      <Boxes size={10} /> Estoque {Math.round(p.estoque_atual)} {p.estoque_atual === 1 ? 'unidade' : 'unidades'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {!data.tem_visitas && (
        <div className="text-xs text-amber-800 bg-amber-100 border border-amber-300 rounded p-2 text-center">
          API de visitas do ML ainda não retornou dados pra hoje (pode demorar alguns minutos pra agregar — atualiza automaticamente).
        </div>
      )}
    </div>
  )
}
